import React, { useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { Button, Dropdown, MenuProps } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as FileSaver from 'file-saver';
import { handleMenuClick, generateMenuItems } from 'utils/excelUtils';
import useCommonStore from 'stores/useCommon';
import { toFormatDate } from 'utils';

interface Props {
  fileName: string;
  dataToExport: any[];
  t: (key: string) => string;
  tourId: string;
  tourName: string;
  tourData: any;
}

const ExportDetailGroup = ({ fileName, dataToExport, tourId, tourName, tourData, t }: Props) => {
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const { currentBranch } = useCommonStore();

  const titleOfReport = 'Báo cáo Chi tiết Tour';

  const exportToCSV = async () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    try {
      setIsLoadingExport(true);

      const workbook = new ExcelJS.Workbook();
      const sheetName = `Tour Detail`;
      const sheet = workbook.addWorksheet(sheetName, {
        views: [
          {
            showGridLines: false
          }
        ]
      });

      // Title
      sheet.mergeCells('A1:H1');
      sheet.getCell('A1').value = currentBranch?.name?.toUpperCase() || '';
      sheet.getCell('A1').font = { bold: true, size: 14 };
      sheet.getCell('A1').alignment = { horizontal: 'center' };

      sheet.mergeCells('A2:H2');
      sheet.getCell('A2').value = `Địa chỉ: ${currentBranch?.address}`;
      sheet.getCell('A2').font = { size: 10 };
      sheet.getCell('A2').alignment = { horizontal: 'center' };

      sheet.mergeCells('A3:H3');
      sheet.getCell('A3').value = currentBranch?.phone_contact
        ? `ĐT: ${currentBranch?.phone_contact}`
        : '';

      sheet.mergeCells('A5:H5');
      sheet.getCell('A5').value = titleOfReport;
      sheet.getCell('A5').font = { size: 12, bold: true };
      sheet.getCell('A5').alignment = { horizontal: 'center' };

      // Tour Information
      sheet.mergeCells('A7:B7');
      sheet.getCell('A7').value = 'Mã Tour:';
      sheet.getCell('A7').font = { bold: true };
      sheet.mergeCells('C7:D7');
      sheet.getCell('C7').value = tourId;
      sheet.getCell('C7').font = { bold: true };

      sheet.mergeCells('A8:B8');
      sheet.getCell('A8').value = 'Tên Tour:';
      sheet.getCell('A8').font = { bold: true };
      sheet.mergeCells('C8:H8');
      sheet.getCell('C8').value = tourName;
      sheet.getCell('C8').font = { bold: true };

      sheet.mergeCells('A9:B9');
      sheet.getCell('A9').value = 'Loại Tour:';
      sheet.getCell('A9').font = { bold: true };
      sheet.mergeCells('C9:D9');
      sheet.getCell('C9').value = tourData?.is_single_tour ? 'Tour Lẻ' : 'Tour Phòng';
      sheet.getCell('C9').font = { bold: true };

      sheet.mergeCells('A10:B10');
      sheet.getCell('A10').value = 'Tổng Khách Hàng:';
      sheet.getCell('A10').font = { bold: true };
      sheet.mergeCells('C10:C10');
      sheet.getCell('C10').value = String(tourData?.total_customers);
      sheet.getCell('C10').font = { bold: true };

      // Add Headers
      sheet.mergeCells('A12:I12');
      sheet.getCell('A12').value = 'DANH SÁCH KHÁCH HÀNG';
      sheet.getCell('A12').alignment = { horizontal: 'center', vertical: 'middle' };
      sheet.getCell('A12').font = { bold: true, size: 12 };

      const headerRow = sheet.getRow(14);
      const headers = [
        'STT',
        'Mã Booking',
        'Chi Nhánh',
        'Ngày Giao Dịch',
        'Tên Khách Hàng',
        'Loại Khách',
        'Số Điện Thoại',
        'Email',
        'Ghi Chú'
      ];

      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.font = { bold: true };
      });
      headerRow.commit();

      sheet.columns = [
        { key: 'A', width: 8 },
        { key: 'B', width: 15 },
        { key: 'C', width: 40 },
        { key: 'D', width: 15 },
        { key: 'E', width: 30 },
        { key: 'F', width: 15 },
        { key: 'G', width: 20 },
        { key: 'H', width: 45 },
        { key: 'I', width: 60 }
      ];

      // Data Rows
      dataToExport.forEach((item, index) => {
        const guestTypeText =
          item.guestType === 'adult' ? 'Người lớn' : item.guestType === 'child' ? 'Trẻ em' : '';
        const row = [
          index + 1,
          item.bookingId,
          item.branchName,
          toFormatDate(item.travelDate),
          item.customerName,
          guestTypeText,
          item.customerPhone,
          item.customerEmail,
          item.note || ''
        ];
        const newRow = sheet.addRow(row);
        newRow.eachCell(cell => {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
      });

      // Add borders
      sheet.eachRow({ includeEmpty: false }, (row, rowIndex) => {
        if (rowIndex >= 14) {
          row.eachCell(cell => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        }
      });

      // Write the file
      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (e) {
      console.error('Error exporting data', e);
    } finally {
      setIsLoadingExport(false);
    }
  };

  const handleClickMenu = (menu: any) => {
    handleMenuClick(menu.key, exportToCSV);
  };
  const items: MenuProps['items'] = useMemo(() => generateMenuItems(), []);

  return (
    <>
      <Dropdown
        menu={{ items, onClick: handleClickMenu }}
        trigger={['click']}
        overlayClassName="menu-user"
      >
        <Button icon={<DownloadOutlined />} loading={isLoadingExport}>
          {t('common.actions.exportData')}
        </Button>
      </Dropdown>
    </>
  );
};

export default ExportDetailGroup;
